import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, LogIn, Ship, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const HERO = "https://images.unsplash.com/photo-1590497008432-598f04441de8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxtYXJpdGltZSUyMGNvbnRhaW5lciUyMGNhcmdvJTIwc2hpcHBpbmclMjBwb3J0JTIweWFyZHxlbnwwfHx8fDE3ODg4MjI5MTF8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate(res.user?.role === "tv" ? "/tv" : "/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:block relative flex-1">
        <img src={HERO} alt="Patio de contenedores" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/85 to-navy-800/70" />
        <div className="absolute bottom-12 left-12 right-12 text-white space-y-4">
          <div className="flex items-center gap-2 text-telemetry">
            <Ship className="w-5 h-5" />
            <span className="text-xs uppercase tracking-[0.3em] font-mono">Control Portuario Inteligente</span>
          </div>
          <h1 className="font-outfit text-4xl xl:text-5xl font-extrabold leading-tight">
            Trazabilidad total de<br />contenedores en tiempo real
          </h1>
          <p className="text-slate-300 text-sm max-w-md">
            Registro OCR, monitoreo de rampas, reportes de tránsito y notificaciones automáticas. Todo en una sola plataforma.
          </p>
        </div>
      </div>

      <div className="flex-1 lg:max-w-xl flex items-center justify-center p-6 bg-gradient-to-b from-slate-50 to-slate-100">
        <div className="w-full max-w-md space-y-8">
          <div className="flex flex-col items-center gap-4">
            <img src="/logo.jpg" alt="SOLOContainer" className="w-20 h-20 rounded-2xl shadow-lg shadow-navy/20" data-testid="login-logo-image" />
            <Logo size="lg" />
          </div>

          <div className="bg-white/80 backdrop-blur-xl border border-white shadow-xl shadow-navy/5 rounded-2xl p-8 space-y-6">
            <div>
              <h2 className="font-outfit text-2xl font-bold">Iniciar Sesión</h2>
              <p className="text-sm text-slate-500">Acceda con su correo corporativo</p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Correo electrónico</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                         placeholder="usuario@solocontainer.app" className="pl-9 h-11" data-testid="login-email-input"
                         autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="password" type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                         placeholder="••••••••" className="pl-9 pr-10 h-11" data-testid="login-password-input"
                         autoComplete="current-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
                  <button type="button" onClick={() => setShowPassword((v) => !v)} data-testid="toggle-password-visibility"
                          aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-navy transition-colors">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error-message">{error}</p>}
              <Button type="submit" disabled={loading} data-testid="login-submit-button"
                      className="w-full h-11 bg-navy hover:bg-navy-800 text-white font-semibold">
                <LogIn className="w-4 h-4 mr-2" /> {loading ? "Verificando..." : "Entrar"}
              </Button>
            </form>
          </div>
          <Footer />
        </div>
      </div>
    </div>
  );
}
